import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TextLink } from "../components/kit/TextLink";
import api, { mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
// Issue #767 PR #920 review (round 1 F1, round 2 R1/R2) — this page is the
// ONE owner of the drawn navy TopBar's flag for this route; see the
// `wantsDrawnCoachChrome` effect below and `lib/coachChromeGate.js`.
import { useRetireTextSize } from "../context/RetireTextSizeContext";
import { computeDrawnCoachChrome } from "../lib/coachChromeGate";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Send, Trash2, Target, NotebookPen, Check, X, Mail, EyeOff, Users, Compass, ClipboardList, BookOpen, ChevronLeft } from "lucide-react";
import { normalizeGoalStatus } from "../lib/goalStatus";
import { coachGoalWrites } from "../lib/coachGoalWrites";
import { coachNoteWrites } from "../lib/coachNoteWrites";
import PlayerHealthPanel from "../components/PlayerHealthPanel";
// Issue #526 — the guides tab's stacked sections fold behind the ONE
// collapsible this repo allows (see SectionCard's header: the hand-rolled
// ones predate the rule; do not add another).
import { SectionCard } from "../components/identityForm";
import LoadFailureNote from "../components/LoadFailureNote";
import PlayerIdentityPanel from "../components/PlayerIdentityPanel";
import PlayerProfileView from "../components/PlayerProfileView";
import { markSurfaceNotificationsRead } from "../lib/notificationsRead";
import { assignGuide, fetchAssignedGuides, fetchPublishedGuides, unassignGuide } from "../lib/guides";
import CoachDrillsPanel from "../components/CoachDrillsPanel";
// Issue #460 — the four list panels, the ribbon and the goal dialog live in
// their own files. Not a style preference: this component measured 833 lines
// against the 800-line `max-lines-per-function` budget in
// `eslint.config.mjs` BEFORE this slice, and that file's header forbids
// raising a budget to make new code fit. The page keeps every loader, every
// piece of state and the whole notification contract; what left is markup.
import CoachPortalShortcuts from "../components/CoachPortalShortcuts";
import CoachGoalsPanel from "../components/CoachGoalsPanel";
import CoachFeedbackPanel from "../components/CoachFeedbackPanel";
import CoachMatchReflectionsPanel from "../components/CoachMatchReflectionsPanel";
import CoachReflectionsPanel from "../components/CoachReflectionsPanel";
// Issue #460, second half — the Career snapshot is its own component now,
// and the one per-player fetch that left this page's #431 generation guard
// went with it. Why that is safe is written on the component: both of its
// fetch sites live inside effects (nothing calls them after a write, unlike
// the five loaders below), so effect cleanup covers the in-flight race, and
// the `key={playerId}` at its call site covers the cross-player one.
import CoachCareerSnapshot from "../components/CoachCareerSnapshot";
import AssignGoalDialog from "../components/AssignGoalDialog";
// Issue #756 — the redrawn empty coach portal, extracted (see that file's
// header for why: this page's function was already at the eslint
// `max-lines-per-function` budget before this slice).
import CoachPortalEmptyState from "../components/CoachPortalEmptyState";
// Issue #767 (epic #749, wave 6) — the redrawn, populated coach's squad.
// Extracted the same way #756/#460 already were (this page's own
// `max-lines-per-function` budget, see the imports above for the
// precedent), and for the same reason: a grouped roster, a squad
// average card and the disabled Pick XI action are a redrawn screen's
// worth of markup, not a few lines inline.
import CoachSquad from "../components/CoachSquad";
// Issue #973 (D186) — the coach's one-player page, behind `coach-player`.
// A new file, not markup grown here: this page is already within a couple
// of eslint's `max-lines-per-function` budget lines of headroom (see the
// budget history atop eslint.config.mjs), and D186.1's own ribbon-and-tabs
// block is duplicated inside it (the SAME elements, the SAME props and
// handlers this page passes today) rather than lifted out of this file,
// because `CoachPortal.test.js` reads that markup straight out of THIS
// file's source and must keep finding it here on the flag-off leg.
import CoachPlayerScreen from "../components/CoachPlayerScreen";
import { coachPlayersName } from "../lib/coachPlayersName";
import { newUi } from "../lib/featureFlags";
import useLaptop from "../lib/useLaptop";
import { usePageWidth } from "../context/PageWidthContext";
import { track } from "../lib/usageTracker";
import {
  FEEDBACK_SORTS,
  GOAL_SORTS,
  MATCH_REFLECTION_SORTS,
  REFLECTION_SORTS,
  defaultSort,
} from "../lib/coachListSort";

const CATEGORIES = [
  { value: "general", label: "General", color: "#64748B" },
  { value: "technique", label: "Technique", color: "#0B2E59" },
  { value: "mindset", label: "Mindset", color: "#D4A017" },
  { value: "fitness", label: "Fitness", color: "#16A34A" },
  { value: "tactical", label: "Tactical", color: "#7C3AED" },
];

// Issue #117 (epic #107): same touch-target convention #106 established in
// PerformanceTracking.jsx / TrainingSessions.jsx — 44px tall on phone
// (Tailwind's default control height, h-9/36px, is under the ~44px comfortable
// tap target), unchanged from `sm:` up.
const TOUCH = "h-11 sm:h-9";

// Issue #197: GOAL_STATUS used to be defined locally here and still
// offered the legacy "active" value as something a coach could mint.
// Now sourced from lib/goalStatus.js — the single shared vocabulary also
// consumed by DevelopmentPlan.jsx (the player-read side of the same
// data) — so "active" is no longer selectable from any surface.

export default function CoachPortal() {
  const { user } = useAuth();
  // Issue #205: reflections are coach-only, including against admin — the
  // one place in this product where admin does not see everything. See
  // api/src/routes/coachReflections.ts for Martin's decision and why it is
  // deliberate.
  //
  // Computed here, ahead of the mount effect below (issue #973, D186.3/
  // D186.4) — that effect's `[]` dependency array captures this from the
  // FIRST render only, the same way it already captures `wantedId` off
  // `sp`, so both facts this page's coach-player leg needs (the role, and
  // the flag) have to exist before that closure is created, not merely
  // before it is called.
  const isCoach = user?.role === "coach";
  // Issue #973 (D186.3/D186.4) — the coach's one-player page. `newUi`
  // itself encodes D186.3's chain (`coach-player` requires `squad`); this
  // adds D186.4 (coaches only — an admin reaches this route too and keeps
  // today's portal).
  const coachPlayerOn = newUi("coach-player") && isCoach;
  const [sp, setSp] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  // #1221 slice 3 (D53 ruling 2): feedback and private notes each have a
  // "loading", "loaded" and "failed", because "No feedback yet" may only be
  // claimed from "loaded". Read by the coach-player page; the shipped page
  // ignores them.
  const [notesStatus, setNotesStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [reflectionsStatus, setReflectionsStatus] = useState("loading");
  // Issue #205 — a coach's own observations, NOT shown to the player. A
  // separate list from `notes` because it is a separate table behind a
  // separate endpoint (see api/src/routes/coachReflections.ts): there is
  // deliberately no shape in which one can be turned into the other by a
  // toggle, which is the whole point of the design.
  const [reflections, setReflections] = useState([]);
  const [reflectionBody, setReflectionBody] = useState("");
  const [editingReflection, setEditingReflection] = useState(null); // {reflection_id, body}
  // Issue #376(b): the PLAYER's own match reflections (`/api/reflections`,
  // api/src/routes/reflections.ts) — a different table, endpoint and
  // audience from `reflections`/`coach-reflections` above, which are the
  // coach's own private observations. Andreas decided 2026-08-17 (see that
  // route's header) that match/season reflections ARE coach-readable, the
  // same as a training reflection — the API has served this to any linked
  // coach since #216 shipped. What was missing was a surface: nothing in
  // this portal ever fetched or rendered it. This is that surface, not a
  // scoping change.
  const [matchReflections, setMatchReflections] = useState([]);
  const [matchReflectionsStatus, setMatchReflectionsStatus] = useState("loading");
  const [goals, setGoals] = useState([]);
  // #1221 slice 2 (D53 ruling 2): "loading", "loaded" and "failed" are three
  // states, because "No goals yet" may only be claimed from "loaded". Read by
  // the coach-player page; the shipped page ignores it.
  const [goalsStatus, setGoalsStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  /**
   * Issue #460 — the order each of the four long lists is shown in.
   *
   * Held HERE rather than inside each panel because a Radix `TabsContent`
   * without `forceMount` unmounts when the coach leaves the tab: state
   * owned by the panel would silently reset to newest-first every time
   * they looked at something else and came back. Same reason `filterCat`
   * below has always lived up here.
   *
   * The orders themselves are `lib/coachListSort.js`; this is only which
   * one is selected.
   */
  const [goalSort, setGoalSort] = useState(defaultSort(GOAL_SORTS));
  const [noteSort, setNoteSort] = useState(defaultSort(FEEDBACK_SORTS));
  const [matchReflectionSort, setMatchReflectionSort] = useState(defaultSort(MATCH_REFLECTION_SORTS));
  const [reflectionSort, setReflectionSort] = useState(defaultSort(REFLECTION_SORTS));
  // Bumped by the ribbon's "Provide feedback" shortcut so the Feedback
  // panel puts the cursor in its composer. A counter, not a flag — see
  // `CoachFeedbackPanel.jsx`.
  const [feedbackFocus, setFeedbackFocus] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [category, setCategory] = useState("general");
  const [filterCat, setFilterCat] = useState("all");
  const [goalDialog, setGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState(defaultGoal());
  const [editingGoal, setEditingGoal] = useState(null); // {goal_id, progress_percentage, status}
  /**
   * Issue #382 — the Quick Reference guides a coach can point this player
   * at, and the ones they already have.
   *
   * `guideCatalogue` is `GET /learn/guides`, which serves ONLY published
   * guides to any signed-in caller — so an unreviewed guide is not in this
   * list for the same reason it is not on the player's page, and the picker
   * below cannot offer one. That is a convenience, not the control: the
   * server refuses an unpublished pair on the write regardless of what this
   * component sends (`api/test/guideAssignments.test.ts`).
   */
  const [guideCatalogue, setGuideCatalogue] = useState([]);
  // Issue #511 (second bug): the catalogue fetch used to swallow its
  // failure into a permanently empty picker rendering "No guides are
  // published yet" — a false claim of empty about a list that was never
  // fetched, the exact #271 class journeys 8d/8e forbid. Three states,
  // because "empty" may only be claimed from "loaded".
  const [catalogueStatus, setCatalogueStatus] = useState("loading"); // "loading" | "loaded" | "failed"
  const [catalogueRetryNonce, setCatalogueRetryNonce] = useState(0);
  const [assignedGuides, setAssignedGuides] = useState([]);
  const [guideToAssign, setGuideToAssign] = useState("");
  const [assigningGuide, setAssigningGuide] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteBusyId, setInviteBusyId] = useState(null);
  // Issue #284: the empty state below is a claim about the roster ("no
  // players yet"), so it may only render once the roster has actually come
  // back. Set on success only — a failed `GET /players` leaves this false
  // and the column blank, exactly as before, rather than telling a coach
  // who may well have players that they have none.
  const [rosterLoaded, setRosterLoaded] = useState(false);

  const loadPlayers = () =>
    api.get("/players").then((r) => {
      setPlayers(r.data);
      return r.data;
    });

  // Issue #138 rework (spec §5): pending invites — links a player has sent
  // this coach that are still `invited`, not `active`. `GET /coach-links`
  // is unfiltered by status (routes/coachLinks.ts), so this is the coach's
  // only discovery mechanism for a pending share until #71 (transactional
  // email) exists.
  const loadInvites = () =>
    // #506: only invites ADDRESSED TO this coach (player-initiated). A
    // coach's own outbound invite (`initiated_by === "coach"`) is the
    // PLAYER's decision — rendering it here would offer an Accept button
    // the API correctly 404s (the accepting party is the one who did NOT
    // initiate). Legacy rows predate `initiated_by` on the wire, so the
    // filter excludes the coach-initiated value rather than requiring
    // "player".
    api
      .get("/coach-links")
      .then((r) => setInvites(r.data.filter((l) => l.status === "invited" && l.initiated_by !== "coach")))
      .catch(() => setInvites([]));

  useEffect(() => {
    loadPlayers().then((rows) => {
      // Issue #74: a `?player=<id>` param (set by the coach Dashboard's
      // roster cards, pages/Dashboard.jsx CoachDashboard) preselects that
      // player; otherwise fall back to the first roster row exactly as
      // before. Read once off the initial roster load — deliberately not a
      // dependency below (`sp` is a fresh object every render) — same
      // pattern as the `[selected]` effect just below.
      const wantedId = sp.get("player");
      const wanted = wantedId ? rows.find((p) => p.player_id === wantedId) : null;
      // Issue #973 (D186 "flag on" step 2) — on the coach-player leg, a
      // bare `/coach-portal` must show the squad with NO player selected,
      // never the first roster row. Every other leg (flag off, or an
      // admin, since `coachPlayerOn` is already `isCoach`-gated) keeps
      // exactly today's `rows[0]` fallback — this condition changes
      // nothing about their output.
      setSelected(wanted || (coachPlayerOn ? null : (rows.length ? rows[0] : null)));
      setRosterLoaded(true);
    });
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Issue #384 item 3 made this effect necessary, and #383 is what it
  // protects. `?player=<id>` used to be read ONCE, on the mount effect
  // above — which was enough while every link carrying it came from a
  // different page (the coach Dashboard's roster cards, #74) and therefore
  // always mounted this one fresh.
  //
  // A coach now LIVES here: it is where they sign in (`lib/homePath.js`),
  // and it is where they open the bell. Clicking a notification
  // (`components/NotificationsBell.jsx` -> `/coach-portal?player=…&tab=…`)
  // is then a same-route navigation: React Router updates the search
  // params, this component does not remount, and the mount effect never
  // runs again. The URL named a player and the page went on showing
  // whichever one was already selected — the notification appearing to do
  // nothing, on the exact path #383 built it for. Caught by journey 17
  // when its coach steps moved off /dashboard.
  //
  // Deliberately narrow: it fires only when the URL NAMES a player that is
  // on the roster and is not the one already showing. A coach who clicks a
  // roster card is untouched — that sets `selected` without writing the
  // URL, so `wantedId` does not change and this does not run.
  const wantedPlayerId = sp.get("player");
  useEffect(() => {
    if (!wantedPlayerId) return;
    setSelected((cur) => {
      if (cur && cur.player_id === wantedPlayerId) return cur;
      return players.find((p) => p.player_id === wantedPlayerId) || cur;
    });
  }, [wantedPlayerId, players]);

  // Issue #973 (D186 step 2) — Back (`cp-player-back`, CoachPlayerScreen.jsx)
  // removes `?player=` to return to the squad, but nothing above CLEARS
  // `selected`: the effect just above only ever sets it, guarded on
  // `wantedPlayerId` being truthy, so a bare `/coach-portal` after Back
  // left the shipped `{selected && …}` detail block (further down this
  // same render) still showing the player who was just left — including
  // its own `cp-selected-name`, which "a bare portal shows the squad and
  // no player" (D186, step 2) forbids. Coach-player leg only: every other
  // leg keeps `selected` exactly as sticky as it has always been (a coach
  // clicking the shipped roster list has never had a "go back to no
  // selection" gesture, and this must not invent one for them).
  useEffect(() => {
    if (coachPlayerOn && !wantedPlayerId) setSelected(null);
  }, [coachPlayerOn, wantedPlayerId]);

  /**
   * TWO READINGS OF THE SAME PARAM, and issue #464 is why they are separate.
   *
   * `chosenTab` is the tab the coach actually ASKED for — null when they
   * merely arrived here. `activeTab` is what the page RENDERS, which needs
   * a default because a bare `/coach-portal` still has to show something.
   *
   * Only the first may clear notifications. Since #384/#416 a coach LANDS
   * here at every sign-in (`lib/homePath.js`) and this page auto-selects
   * `rows[0]`, so passing the DEFAULTED value below marked
   * `player_goal_achieved` read for that player before the coach had
   * looked at anything — the bell reported nothing and the only way to
   * find out something had happened was to open the panel, which is
   * exactly what #464 reported.
   */
  const chosenTab = sp.get("tab");
  // Issue #590 — the strip was reordered (Identity now first), but the bare
  // `/coach-portal` landing tab deliberately STAYS on Goals: a coach lands
  // where they act, not on Identity (Andreas' call, resolving the issue's
  // open question). A first tab that isn't the selected one is normal; this
  // leaves #464's notification contract untouched (`chosenTab` unchanged).
  const activeTab = chosenTab || "goals";
  const selectedPlayerId = selected?.player_id || null;
  /**
   * Issue #417: "going to it on the platform", for the coach's three types.
   *
   * SCOPED TO THE SELECTED PLAYER, which is the whole reason this passes an
   * id at all. A coach's feed unions every actively-linked player, so
   * clearing `player_goal_achieved` wholesale on the Goals tab would
   * silently dismiss every other player's achievement the moment the coach
   * looked at one of them — a bell that lies in the direction of hiding
   * things, which is the worse direction.
   *
   * Coach only. An admin can reach this page and has no feed at all (the
   * API 404s them from every notification route on purpose — see
   * `routes/notifications.ts` on why admin does not get the unfiltered
   * roster), so asking would be a request whose only possible answer is an
   * error.
   *
   * Re-runs on a same-route navigation, which is the common path: clicking
   * a notification lands here with a new `?player=&tab=` and does NOT
   * remount this component (#384's finding, journey 17).
   *
   * `chosenTab`, never `activeTab` (#464): an EXPLICIT tab is the gesture
   * Martin asked for, and landing on the default is not one. A null tab
   * clears nothing — `typesForSurface` tolerates it by design.
   */
  useEffect(() => {
    if (user?.role !== "coach" || !selectedPlayerId) return;
    markSurfaceNotificationsRead("coach-portal", chosenTab, selectedPlayerId);
  }, [user?.role, chosenTab, selectedPlayerId]);

  const acceptInvite = async (link) => {
    setInviteBusyId(link.id);
    try {
      await api.post(`/coach-links/${link.id}/accept`);
      toast.success(`${link.player_full_name} is now linked`);
      await loadInvites();
      // Issue #284: only the mount effect ever set `selected`, so a coach who
      // accepted their first invite went from the empty state straight to a
      // blank detail column — the roster had a row, nothing was selected, and
      // `{selected && …}` rendered nothing. Accepting is the one action the
      // empty state names, so it must not land back on a blank page. Existing
      // selections are left alone.
      const rows = await loadPlayers();
      // Issue #973 (D186 step 2) — the same coach-player exception as the
      // mount effect above: this leg leaves `selected` null rather than
      // falling back to `rows[0]`.
      setSelected((cur) => cur || (coachPlayerOn ? null : (rows.length ? rows[0] : null)));
    } catch {
      toast.error("Failed to accept invite");
    }
    setInviteBusyId(null);
  };

  const declineInvite = async (link) => {
    setInviteBusyId(link.id);
    try {
      await api.delete(`/coach-links/${link.id}`);
      toast.success("Invite declined");
      loadInvites();
    } catch {
      toast.error("Failed to decline invite");
    }
    setInviteBusyId(null);
  };

  // The published catalogue does not depend on which player is selected, so
  // it is fetched once rather than on every roster click — and re-fetched
  // only by the failure note's Retry (#511).
  useEffect(() => {
    let cancelled = false;
    setCatalogueStatus("loading");
    fetchPublishedGuides()
      .then((rows) => {
        if (cancelled) return;
        setGuideCatalogue(rows);
        setCatalogueStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setCatalogueStatus("failed");
      });
    return () => { cancelled = true; };
  }, [catalogueRetryNonce]);

  /**
   * ============================================================
   * Issue #431 — WHY EVERY LOADER BELOW IS GENERATION-GUARDED
   * ============================================================
   * Selecting a player fires five independent requests from this page
   * (six before #460 moved `/stats/summary` into CoachCareerSnapshot,
   * which closes the same race by effect cleanup + a `key` remount — see
   * its header). Before #431, not one of them checked, on arrival, whether
   * the player it was fetched for was still the player on screen. Click
   * roster row A, then B before A answers, and whichever response lands
   * LAST wins — so a `/notes` reply for A could call `setNotes(A)` while
   * the header already read B. A coach shown one player's private feedback
   * under another player's name is the worst outcome this page has
   * available, and `/coach-reflections` and `/reflections` carry the same
   * exposure.
   *
   * The window is not theoretical. `?player=` is also read on SAME-ROUTE
   * navigation (the effect above, #384) — which is the notification path
   * #383/#417 built — so clicking two bell items in succession arranges
   * this race with two clicks and no unusual latency.
   *
   * A GENERATION COUNTER, not the `let cancelled = false` idiom
   * `Dashboard.jsx` uses. That idiom guards a fetch STARTED INSIDE the
   * effect, via the effect's own cleanup. These six loaders are also called
   * from outside any effect — `addNote`/`deleteNote` call `loadNotes`,
   * `saveGoal`/`deleteGoal` call `loadGoals`, `assignSelectedGuide` calls
   * `loadAssignedGuides`, and so on — and a flag scoped to an effect run
   * cannot see those calls at all, which is precisely where a coach is most
   * likely to switch players (post feedback, then click the next name). A
   * ref-held counter is owned by the component rather than by one effect
   * run, so ONE mechanism covers every call site.
   *
   * The counter is bumped on selection change, and each loader captures its
   * value at call time and drops both the fulfilment and the failure if the
   * count has moved on. `guardedLoad` is that wrapper; nothing here should
   * call `set*` outside it.
   */
  const loadGen = useRef(0);

  /**
   * Run one loader for the current generation. `apply` is called with the
   * response data, `onFailure` with nothing, and NEITHER is called if the
   * selection changed while the request was in flight.
   *
   * `onFailure` CLEARS rather than retains, always. A loader that leaves
   * the previous value on a failed request is the same defect as the race,
   * just with a 500 instead of a slow response: the last player's notes stay
   * under this player's name, and with fetch-on-select only, they stay there
   * until the coach clicks something else.
   */
  const guardedLoad = (request, apply, onFailure) => {
    const gen = loadGen.current;
    return request()
      .then((r) => { if (loadGen.current === gen) apply(r.data); })
      .catch(() => { if (loadGen.current === gen) onFailure(); });
  };

  useEffect(() => {
    if (!selected) return;
    // Bump FIRST: every loader `loadAll` starts reads this value, and every
    // response still in flight for the previous player is now stale.
    loadGen.current += 1;
    // Then clear, so the previous player's data is not rendered under this
    // player's name for however long their six requests take. Dropping the
    // stale response is not enough on its own — until the new one lands,
    // what is on screen still belongs to the player before.
    setNotes([]); setNotesStatus("loading");
    setReflections([]); setReflectionsStatus("loading");
    setMatchReflections([]); setMatchReflectionsStatus("loading");
    setGoals([]);
    setGoalsStatus("loading");
    setAssignedGuides([]);
    // #1356: and the composers. Typed text and an edit in progress belong to
    // the player they were written for; left in state they would be posted
    // under the next player's name. Shared by both looks.
    setFeedback("");
    setReflectionBody("");
    setEditingReflection(null);
    setEditingGoal(null);
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const loadAll = () => {
    loadNotes();
    loadReflections();
    loadMatchReflections();
    loadGoals();
    loadAssignedGuides();
  };

  // Issue #382. Scoped to the selected player by the API's own
  // `resolveScope` — a coach reads this only for a player they hold an
  // ACTIVE link to, so an empty list here is "nothing assigned", never
  // "you are not allowed" quietly rendered as the same thing.
  const loadAssignedGuides = () => {
    const playerId = selected.player_id;
    return guardedLoad(
      () => fetchAssignedGuides(playerId).then((rows) => ({ data: rows })),
      setAssignedGuides,
      () => setAssignedGuides([]),
    );
  };

  const loadNotes = () => {
    const playerId = selected.player_id;
    return guardedLoad(
      () => api.get(`/notes?player_id=${playerId}`),
      (rows) => { setNotes(rows); setNotesStatus("loaded"); },
      () => { setNotes([]); setNotesStatus("failed"); },
    );
  };
  const retryNotes = () => { setNotesStatus("loading"); loadNotes(); };

  // Issue #376(b): admin reaches this route too and is authorized for this
  // exact same request under the STANDARD scoping `resolveScope` applies
  // (admin bypasses the coach-link check) — unlike `/coach-reflections`
  // just below, which denies admin by deliberate design. So this fetch,
  // unlike `loadReflections`, is not gated on `isCoach` at all: admin sees
  // it too, per the decision recorded in `api/src/routes/reflections.ts`.
  const loadMatchReflections = () => {
    const playerId = selected.player_id;
    return guardedLoad(
      () => api.get(`/reflections?player_id=${playerId}&kind=match`),
      (rows) => { setMatchReflections(rows); setMatchReflectionsStatus("loaded"); },
      () => { setMatchReflections([]); setMatchReflectionsStatus("failed"); },
    );
  };
  const retryMatchReflections = () => { setMatchReflectionsStatus("loading"); loadMatchReflections(); };

  // Issue #205: coach-only by design — admin is denied at the API (403),
  // which is deliberate and not a bug (see the route's header comment). So
  // this doesn't fire for a non-coach at all, rather than firing and
  // swallowing an expected error; the Reflections tab renders an
  // explanation for admin instead of an empty list. Already newest-first
  // from the server, so no client-side sort.
  const loadReflections = () => {
    if (user?.role !== "coach") { setReflectionsStatus("loaded"); return setReflections([]); }
    const playerId = selected.player_id;
    return guardedLoad(
      () => api.get(`/coach-reflections?player_id=${playerId}`),
      (rows) => { setReflections(rows); setReflectionsStatus("loaded"); },
      () => { setReflections([]); setReflectionsStatus("failed"); },
    );
  };
  const retryReflections = () => { setReflectionsStatus("loading"); loadReflections(); };

  const loadGoals = () => {
    const playerId = selected.player_id;
    return guardedLoad(
      () => api.get(`/goals?player_id=${playerId}`),
      (rows) => { setGoals(rows); setGoalsStatus("loaded"); },
      () => { setGoals([]); setGoalsStatus("failed"); },
    );
  };
  const retryGoals = () => { setGoalsStatus("loading"); loadGoals(); };

  // #1221 slice 3: the requests of the note writes throw on failure and toast
  // nothing (`lib/coachNoteWrites.js`), so the coach-player page shows a failure
  // where it happened (D57). The shipped page's handlers below wrap them with
  // the toast and `confirm()` they have always had.
  const { postNote, removeNote, postReflection, patchReflection, removeReflection } = coachNoteWrites({
    selected, feedback, category, reflectionBody, editingReflection, setFeedback, setCategory,
    setReflectionBody, setEditingReflection, loadNotes, loadReflections,
  });

  const addNote = async () => {
    if (!feedback.trim() || !selected) return;
    try {
      await postNote();
      toast.success("Feedback added");
    } catch { toast.error("Failed to add feedback"); }
  };

  const togglePin = async (n) => {
    try { await api.patch(`/notes/${n.note_id}`, { pinned: !n.pinned }); loadNotes(); }
    catch { toast.error("Failed"); }
  };

  const deleteNote = async (n) => {
    if (!confirm("Delete this feedback note?")) return;
    try { await removeNote(n); toast.success("Note deleted"); }
    catch { toast.error("Failed"); }
  };

  // ---- Issue #205: reflections (not shown to the player) ----
  const addReflection = async () => {
    if (!reflectionBody.trim() || !selected) return;
    try {
      await postReflection();
      toast.success("Reflection saved — not shown to the player");
    } catch { toast.error("Failed to save reflection"); }
  };

  const saveReflectionEdit = async () => {
    if (!editingReflection?.body.trim()) return;
    try {
      await patchReflection();
      toast.success("Reflection updated");
    } catch { toast.error("Failed to update reflection"); }
  };

  const deleteReflection = async (r) => {
    if (!confirm("Delete this reflection?")) return;
    try { await removeReflection(r); toast.success("Reflection deleted"); }
    catch { toast.error("Failed"); }
  };

  // ---- Issue #382: assign a published guide to this player ----
  const assignSelectedGuide = async () => {
    if (!guideToAssign || !selected) return;
    const [category, slug] = guideToAssign.split("/");
    setAssigningGuide(true);
    try {
      await assignGuide(selected.player_id, category, slug);
      setGuideToAssign("");
      await loadAssignedGuides();
      toast.success(`Assigned to ${selected.full_name}`);
    } catch {
      // One message for both refusals. The server deliberately does not
      // distinguish "that guide is not published" from "that is not your
      // player" (anti-enumeration, api/src/routes/authz.ts), so neither
      // does this — inventing a reason here would invent the distinction.
      toast.error("Could not assign that guide. Refresh and try again.");
    }
    setAssigningGuide(false);
  };

  const removeAssignedGuide = async (a) => {
    try {
      await unassignGuide(a.assignment_id);
      await loadAssignedGuides();
      toast.success("Assignment removed");
    } catch { toast.error("Failed to remove assignment"); }
  };

  const openGoalDialog = () => { setNewGoal(defaultGoal()); setGoalDialog(true); };

  // #1221 slice 2: the requests of the three goal writes throw on failure and
  // toast nothing (`lib/coachGoalWrites.js`), so the coach-player page shows a
  // failure where it happened (D57). The shipped page's handlers below wrap
  // them with the toast and `confirm()` they have always had.
  const { createGoal, putGoalProgress, removeGoal } = coachGoalWrites({
    selected, newGoal, editingGoal, setEditingGoal, setGoalDialog, loadGoals,
  });

  const saveGoal = async () => {
    if (!newGoal.goal_title.trim() || !selected) return toast.error("Goal title required");
    try {
      await createGoal();
      toast.success(`Goal assigned to ${selected.full_name.split(" ")[0]}`);
    } catch { toast.error("Failed to create goal"); }
  };

  const beginEditGoal = (g) => setEditingGoal({
    goal_id: g.goal_id,
    progress_percentage: g.progress_percentage ?? 0,
    // Issue #197: normalize here, not just at display time. GOAL_STATUSES
    // no longer has an "active" option, so a legacy row's raw g.status
    // wouldn't match any <SelectItem> and the dropdown would render blank
    // instead of showing "In Progress" the way the badge next to it does.
    status: normalizeGoalStatus(g.status),
  });

  const saveGoalProgress = async () => {
    if (!editingGoal) return;
    try {
      await putGoalProgress();
      toast.success("Progress updated");
    } catch { toast.error("Failed to update progress"); }
  };

  // #1221 slice 2 (D223.1): `removeGoal` is the delete without the question; the
  // coach-player page asks in the page (`ConfirmDeleteDialog`) and calls it.
  // `deleteGoal` is the shipped page's, which asks with `confirm()`.
  const deleteGoal = async (g) => {
    if (!confirm(`Delete "${g.goal_title}"?`)) return;
    try { await removeGoal(g); toast.success("Goal removed"); }
    catch { toast.error("Failed"); }
  };

  const tab = activeTab;

  /**
   * Select a tab by writing `?tab=`, which is exactly what clicking the tab
   * strip does — so the ribbon's "Provide feedback" and "Assign coaching
   * guide" shortcuts (#460) land the coach in the flows those tabs already
   * hold, rather than in a second copy of them.
   *
   * It goes through `chosenTab` like every other explicit tab choice, so a
   * shortcut IS the deliberate gesture #464 distinguishes from merely
   * arriving — and neither of the two tabs it targets accounts for a
   * notification type anyway (`SURFACE_NOTIFICATION_TYPES`).
   */
  const goToTab = (v, { focus = false } = {}) => {
    const n = new URLSearchParams(sp);
    n.set("tab", v);
    setSp(n, { replace: true });
    if (focus) setFeedbackFocus((c) => c + 1);
  };

  // Issue #204: for a coach, `GET /notes` is now author-scoped server-side
  // (see api/src/routes/notes.ts's header comment — a player can be linked
  // to two coaches from two different clubs, so an unscoped read would leak
  // one coach's notes to the other, which is the exact cross-club leak #204
  // exists to close). That makes this check mostly a no-op for a coach —
  // every note in `notes` is already theirs — but it stays here because
  // admin's `notes` list is still unscoped (full visibility, by design), so
  // admin needs this to keep seeing pin/delete on every note while a coach
  // naturally only ever sees their own. PATCH/DELETE on a note a coach
  // doesn't own now 404s, matching the read no longer surfacing it at all.
  const canMutateNote = (n) => user?.role === "admin" || n.coach_id === user?.id;

  // Issue #767 PR #920 review round 1 (F1) and round 2 (R1/R2) — the drawn
  // navy TopBar (`Layout.jsx`'s `drawnTopBar`/`retiredByScreen`) used to be
  // declared by whichever REDRAWN CHILD happened to mount
  // (`CoachPortalEmptyState.jsx` AND `CoachSquad.jsx`, independently) —
  // TWO writers of one boolean, which is exactly the hazard
  // `RetireTextSizeContext.js`'s own header warns against ("two screens
  // both leaving it `true` on unmount would fight each other"). Round 1
  // fixed `CoachSquad.jsx`'s own half (it mounted too late, after
  // `rosterLoaded`, to close journey 17's race) but left
  // `CoachPortalEmptyState.jsx` writing independently — round 2's own
  // reproduction: a coach with an empty roster (both flags on) accepts a
  // pending invite IN PLACE, `CoachPortalEmptyState` unmounts, its cleanup
  // clears the flag, and nothing sets it back — the drawn chrome silently
  // reverted to the shipped header until a reload (R1).
  //
  // `CoachPortal.jsx` is now the ONE owner. `computeDrawnCoachChrome`
  // (`lib/coachChromeGate.js`) is the pure decision — see that file's own
  // header for the full ruling (R2): both flags on draws the bar from
  // first paint regardless of the roster (keeping round 1's own fix, since
  // SOME rebuilt screen is guaranteed to render either way); exactly one
  // flag on follows the roster once `rosterLoaded` resolves, shipped while
  // it is still unknown — a real, late swap, matching `main`'s existing
  // single-flag behaviour; neither flag, or not a coach, never draws it.
  // `CoachPortalEmptyState.jsx` and `CoachSquad.jsx` both declare nothing
  // themselves any more.
  const wantsDrawnCoachChrome = computeDrawnCoachChrome({
    isCoach,
    emptyScreenOn: newUi("coach-portal-empty"),
    squadScreenOn: newUi("squad"),
    rosterLoaded,
    playerCount: players.length,
  });
  const { setRetired: setCoachChromeRetired } = useRetireTextSize();
  useEffect(() => {
    if (!wantsDrawnCoachChrome) return;
    setCoachChromeRetired(true);
    return () => setCoachChromeRetired(false);
  }, [wantsDrawnCoachChrome, setCoachChromeRetired]);

  // Issue #756 — the redrawn empty coach portal (`CoachPortalEmptyState.jsx`)
  // replaces both this page's own header AND the top-of-page invites card
  // ONLY for the exact case it draws: a coach, roster genuinely loaded, and
  // zero players. Any other combination (admin, a coach WITH players, or the
  // flag off) renders exactly the shipped markup below, untouched — the
  // populated portal stays out of scope (wave 6, #767), and admin's roster
  // is never a set of links (see the comment above `cp-empty-roster` further
  // down). `CoachPortalEmptyState` renders the invites section itself, in
  // the SAME position this page puts it (above the empty-roster block) when
  // something's pending, plus a labelled-empty box when nothing is (#756
  // item 5; #795 review round F2 — an earlier cut moved the populated
  // card's position too, which is what F2 fixed) — so it must not also
  // render here.
  const showEmptyPortalRedraw = isCoach && rosterLoaded && players.length === 0 && newUi("coach-portal-empty");

  // Issue #767 — the redrawn, POPULATED squad. `rosterLoaded &&
  // players.length > 0` is the exact complement of `showEmptyPortalRedraw`'s
  // own `=== 0` gate (never both true at once), gated on the same
  // `isCoach` (admin's roster stays the shipped flat list — the drawing is
  // a coach-only screen, brief-767.md "Where it lives"). Behind its own
  // flag, `newUi("squad")`, independent of `coach-portal-empty`'s.
  const showSquadScreen = isCoach && rosterLoaded && players.length > 0 && newUi("squad");

  // Issue #973 (D186.1, D168 ruling 9) — the squad row's own tap target.
  // Off this leg, unchanged: `onSelectPlayer={setSelected}`, exactly as
  // `CoachSquad.jsx` has always been called (its own props are untouched
  // either way). On it, a tap writes `?player=` instead — a real
  // navigation (`setSp`, push, not `replace`), so the phone's Back button
  // returns to the squad the same way D35 already requires this page's
  // other list-reached screens to behave.
  const goToPlayer = (p) => { const n = new URLSearchParams(sp); n.set("player", p.player_id); setSp(n); };
  const selectPlayerOnSquad = coachPlayerOn ? goToPlayer : setSelected;

  // Issue #973 (D29/D35/D186) — the coach's one-player page. An early
  // return, the `DevelopmentPlan.jsx` shape (#940, PR #957): every line
  // above still runs on every leg, and this fires only once the flag, the
  // coach role and an explicit `?player=` are ALL true — replacing the
  // WHOLE shipped page for that case. The loading shell, the D186.2
  // not-found card and the ~40-prop `CoachPlayerScreen` handoff live in
  // `renderCoachPlayerRoute` below (a SIBLING function in this same file,
  // kept out of THIS function's own eslint `max-lines-per-function` count
  // — see the header atop eslint.config.mjs).
  // #1430 (D386): from the laptop tier a chosen player's page opens beside the squad, which becomes the 380px
  // list column. With no player chosen the squad keeps the page's full row (#1426), and below the laptop tier the
  // page replaces the squad, as D35 says (Back returns to it).
  const laptop = useLaptop();
  const splitOn = laptop && coachPlayerOn && showSquadScreen && Boolean(wantedPlayerId);
  // #1471 (D395): the coach shell has no sidebar, so the squad takes the wide column (1040px) from the laptop tier; below it the one 640px column.
  usePageWidth(laptop && showSquadScreen ? "wide" : "narrow");
  const playerRoute = () => renderCoachPlayerRoute({
      wantedPlayerId, rosterLoaded, players, user,
      goals, goalsStatus, retryGoals, goalSort, setGoalSort, editingGoal, beginEditGoal, setEditingGoal, putGoalProgress, removeGoal, openGoalDialog,
      notes, notesStatus, retryNotes, noteSort, setNoteSort, filterCat, setFilterCat, canMutateNote, togglePin, removeNote,
      feedback, setFeedback, category, setCategory, postNote, feedbackFocus, setFeedbackFocus,
      guideCatalogue, catalogueStatus, setCatalogueRetryNonce, assignedGuides, guideToAssign, setGuideToAssign, assigningGuide, assignSelectedGuide, removeAssignedGuide,
      matchReflections, matchReflectionsStatus, retryMatchReflections, matchReflectionSort, setMatchReflectionSort, reflections, reflectionsStatus, retryReflections, reflectionSort, setReflectionSort, isCoach,
      editingReflection, setEditingReflection, patchReflection, removeReflection, reflectionBody, setReflectionBody, postReflection,
      tab, goToTab, goalDialog, setGoalDialog, newGoal, setNewGoal, createGoal, besideSquad: splitOn,
    });
  if (coachPlayerOn && wantedPlayerId && !splitOn) {
    return playerRoute();
  }

  return (
    // Beside the squad the player's page carries `coach-portal-page` itself (`CoachPlayerScreen`'s root), so this one yields it.
    <div className="space-y-6" data-testid={splitOn ? undefined : "coach-portal-page"}>
      {!showEmptyPortalRedraw && !showSquadScreen && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Coaching workspace</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">Coach Portal</h1>
        </div>
      )}

      {/* Issue #138 rework (spec §5): pending invites — a player who shared
          with this coach but hasn't been accepted yet. Only rendered when
          there's something to act on, same "curated, not exhaustive"
          footprint as the rest of this page. Suppressed here when
          `showEmptyPortalRedraw` — that composed screen renders its own
          invites section, repositioned per the drawing (#756 item 5).
          NOT suppressed by `showSquadScreen` — `Squad.dc.html` draws no
          invites section at all, but accepting a pending invite is real,
          undrawn functionality this slice does not remove (departure,
          named on the PR); it keeps rendering exactly where it always has,
          above the redrawn roster. */}
      {!showEmptyPortalRedraw && invites.length > 0 && (
        <Card className="border-[#D4A017]/40 bg-[#D4A017]/5" data-testid="cp-invites-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 px-1">
              <Mail size={15} color="#8a6a10" />
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#8a6a10]">
                Pending invites ({invites.length})
              </span>
            </div>
            <div className="space-y-2" data-testid="cp-invites-list">
              {invites.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-[#D4A017]/30 bg-white p-3 flex-wrap"
                  data-testid={`cp-invite-${inv.id}`}
                >
                  <div className="font-semibold text-slate-900">{inv.player_full_name}</div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => acceptInvite(inv)}
                      disabled={inviteBusyId === inv.id}
                      className={`${TOUCH} px-3`}
                      style={{ background: "#16A34A" }}
                      data-testid={`cp-invite-accept-${inv.id}`}
                    >
                      <Check size={14} className="mr-1" /> Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => declineInvite(inv)}
                      disabled={inviteBusyId === inv.id}
                      className={`${TOUCH} px-3`}
                      data-testid={`cp-invite-decline-${inv.id}`}
                    >
                      <X size={14} className="mr-1" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className={splitOn ? "nw-split" : "grid lg:grid-cols-4 gap-6"} data-testid={splitOn ? "coach-split" : undefined}>
        {/* Player list */}
        {/* `min-w-0` — issue #251. #205 fixed the DETAIL column below and
            left this one, its sibling in the same `lg:grid-cols-4` grid:
            the rows inside carry `truncate` on the player's name and role,
            which is `white-space: nowrap`, so their min-content is the whole
            string and this track would refuse to shrink below the longest
            name on the roster. The truncation only starts working once the
            track can. */}
        {/* Issue #803 (narrow part only): this card's `lg:col-span-1` sibling
            is what #756's redrawn empty portal mounts INSIDE — the sibling
            itself is not the redraw and was never suppressed by it, so at
            390 it rendered above the redraw as an empty "PLAYERS (0)" card
            that is not in the drawing at all. Suppressed here under the
            same `showEmptyPortalRedraw` condition that already gates the
            rest of this screen's shipped-vs-redrawn split, and ONLY then:
            a coach with players, an admin, or the flag off all still get
            this card exactly as shipped. The larger question #803 actually
            asks — replace the page or sit inside it, `CHROMELESS_ROUTES`,
            the missing `TopBar`, the invites card's page-level position —
            is out of scope here; see the PR body.

            Issue #767: also suppressed under `showSquadScreen` — that flag
            replaces this exact card with `CoachSquad`, the redrawn,
            grouped roster (rendered as this card's own sibling, just
            below), rather than growing a third branch inside it. */}
        {!showEmptyPortalRedraw && !showSquadScreen && (
          <Card className="min-w-0 border-slate-200 lg:col-span-1">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3 px-2">Players ({players.length})</div>
              <div className="space-y-1" data-testid="player-list">
                {/* Issue #719 — the way in to a player's record. A SIBLING of
                    the select button, never nested inside it: an <a> inside a
                    <button> is invalid markup and the browser's own handling
                    of it varies. The button keeps its `player-<id>` testid
                    (journeys 02/03/07/12/13/17/22 assert it) and its
                    selecting behaviour unchanged; the link is the second,
                    smaller target. `min-w-0` on the row for the reason the
                    column above documents — the names inside truncate, so
                    nothing here may set a min-content width. */}
                {players.map((p) => (
                  <div key={p.player_id}
                    className={`flex items-center gap-1 rounded-lg min-w-0 ${selected?.player_id === p.player_id ? "bg-[#0B2E59] text-white" : "hover:bg-slate-100"}`}>
                    <button onClick={() => { track("coach.player_open"); setSelected(p); }}
                      data-testid={`player-${p.player_id}`}
                      className="flex-1 flex items-center gap-3 p-2.5 rounded-lg transition-all text-left min-w-0">
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={mediaUrl(p.profile_photo)} /><AvatarFallback>{p.full_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{p.full_name}</div>
                        <div className={`text-xs truncate ${selected?.player_id === p.player_id ? "text-white/70" : "text-slate-500"}`}>{p.playing_role}</div>
                      </div>
                    </button>
                    <Link
                      to={`/coach-portal/record?player=${encodeURIComponent(p.player_id)}`}
                      title={`Open ${p.full_name}'s record`}
                      aria-label={`Open ${p.full_name}'s record`}
                      data-testid={`player-record-${p.player_id}`}
                      className={`shrink-0 mr-1.5 p-2 rounded-md ${selected?.player_id === p.player_id ? "text-white/80 hover:text-white hover:bg-white/10" : "text-slate-400 hover:text-[#0B2E59] hover:bg-slate-200"}`}>
                      <ClipboardList size={16} />
                    </Link>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Issue #767 — the redrawn, grouped squad. At phone-390,
            `lg:grid-cols-4`'s own mobile-first default collapses this to
            one column, matching the artboard's own full-width 390px
            composition; the detail column below it is untouched (ruling 9).

            Issue #1426: at `lg` and up this takes the whole row
            (`lg:col-span-4`), not the roster card's `lg:col-span-1` slot it
            first borrowed. The squad was built for a 390px column and has no
            width logic of its own; in a quarter of the page it measured
            152px at 1024 and 256px at 1440 and wrapped one word per line.
            `lg:col-span-3` (the issue's first option) was measured too and
            is not enough: 504px at 1024, under the 600px floor. The detail
            column wraps to the row below it. Below 1024 the grid is a single
            column, so nothing changes at 390/360. */}
        {showSquadScreen && (
          <div className={splitOn ? "min-w-0" : "min-w-0 lg:col-span-4"} data-coach-squad-holder="">
            <CoachSquad players={players} selectedId={splitOn ? wantedPlayerId : selected?.player_id} onSelectPlayer={selectPlayerOnSquad} />
          </div>
        )}

        {/* Detail */}
        {/* `min-w-0` (issue #205, same class of fix as #141's note in the
            Feedback composer below): a grid item defaults to
            `min-width: auto`, i.e. it refuses to shrink below its content,
            so the widest thing inside this column sets the track width and
            the whole page scrolls horizontally. The TabsList already has
            `max-w-full overflow-x-auto` to scroll internally, but
            `max-w-full` resolves against a parent that has itself grown —
            so it never engaged. Adding a THIRD tab trigger (Reflections) is
            what first pushed the strip past 390px and made this visible;
            the responsive harness caught it on every /coach-portal cell at
            phone-390, including the two tabs that predate this slice. */}
        <div className={splitOn ? "space-y-6 min-w-0" : "lg:col-span-3 space-y-6 min-w-0"} data-nw-detail={splitOn ? "" : undefined}>
          {splitOn && playerRoute()}
          {!splitOn && selected && (
            <>
              {/* Player profile header — issue #508. The ad-hoc badge
                  cluster (photo + role·country + batting/bowling/club/school
                  badges) is now `PlayerProfileView`, the read-only mirror of
                  `CoachProfileView`: a coach reads a player through a real
                  profile view with the same optional-row rhythm. The name
                  keeps its `cp-selected-name` handle via `nameTestId` —
                  journeys 02/03/07/12/13/17/22 assert it. This is the
                  PROFILE header; the shared IDENTITY sections stay on the
                  Identity tab (#460), not duplicated here. */}
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <PlayerProfileView
                    player={selected}
                    testId="cp-player-profile"
                    nameTestId="cp-selected-name"
                  />
                </CardContent>
              </Card>

              {/* Issue #460 — the top ribbon. "Assign goal" was the only
                  shortcut here and it lived inside the header card above;
                  it keeps its `cp-assign-goal` testid and still opens the
                  same dialog. The other three point at flows that already
                  exist — see `lib/coachShortcuts.js` for which, and why
                  none of them is a second implementation. */}
              <CoachPortalShortcuts
                playerId={selected.player_id}
                onOpenGoalDialog={openGoalDialog}
                onGoToTab={goToTab}
              />

              {/* Issue #287 — the health record, ABOVE the career snapshot
                  and outside the tab strip. Deliberately not a tab: a fact
                  a coach has to remember to go and look for is a fact they
                  find out about afterwards, and "before something happens
                  rather than after" is the whole ticket. The emergency
                  contact sharpens it — the moment it is needed is the
                  moment nobody is navigating a tab strip. It renders for
                  admin too — with an explanation instead of data — because
                  admin being excluded is a decision worth stating in the
                  product rather than a surface that silently isn't there
                  (the #205 posture, same as the Reflections tab below).
                  The API denies admin outright; this is not a UI hide. */}
              <PlayerHealthPanel
                playerId={selected.player_id}
                playerName={selected.full_name}
                viewerRole={user?.role}
              />

              {/* Career snapshot — issue #460: the player profile's own
                  three dashboards (Career highlights, Bowling record,
                  Fielding & wicket-keeping) behind the player profile's own
                  filters, rendered by the SAME components Statistics.jsx
                  uses. `key` is what resets its filters and cancels its
                  in-flight requests on a roster click — see the component's
                  header before removing it. */}
              <CoachCareerSnapshot key={selected.player_id} playerId={selected.player_id} />

              <Tabs
                value={tab}
                onValueChange={goToTab}
                className="space-y-4"
              >
                <TabsList data-testid="cp-tabs">
                  {/* Issue #590 — Martin: "put Identity before goals and
                      move reflections to last". Strip order is Identity,
                      Goals, Feedback, Guides, Match Reflections, Reflections
                      — both reflection tabs sit last, together. The bare
                      /coach-portal landing tab stays on Goals (see the
                      `activeTab` default): order changed, not the tab a
                      coach lands on. */}
                  {/* Issue #292. A TAB and not an always-open card — the
                      deliberate opposite of the health panel above. That one
                      is outside the strip because a coach who has to
                      remember to look for an allergy finds out afterwards;
                      nothing here is needed in a hurry, and the objection
                      #287 weighed and overruled — somebody's private writing
                      open on a phone at a public ground — wins on this
                      record. Rendered for admin too, with an explanation
                      instead of content, per the #205 posture the
                      Reflections tab beside it uses. */}
                  <TabsTrigger value="identity" data-testid="cp-tab-identity">
                    <Compass size={14} className="mr-1.5" />Identity
                  </TabsTrigger>
                  <TabsTrigger value="goals" data-testid="cp-tab-goals"><Target size={14} className="mr-1.5" />Goals ({goals.length})</TabsTrigger>
                  <TabsTrigger value="feedback" data-testid="cp-tab-notes"><NotebookPen size={14} className="mr-1.5" />Feedback ({notes.length})</TabsTrigger>
                  {/* Issue #382 — Martin: "enable a coach to assign a Quick
                      Reference guide to a player". Rendered for admin too,
                      who can assign under the same standard scoping every
                      other write on this page uses. */}
                  <TabsTrigger value="guides" data-testid="cp-tab-guides">
                    <BookOpen size={14} className="mr-1.5" />Guides ({assignedGuides.length})
                  </TabsTrigger>
                  {/* Issue #376(b). The player's own match reflections —
                      readable by any linked coach per the decision in
                      `api/src/routes/reflections.ts`, and by admin too
                      (that route's standard scoping, not the #205
                      admin-excluded posture the "Reflections" tab below
                      uses). A separate tab, not folded into "Reflections":
                      that tab is this coach's own private notes about the
                      player, and putting the player's own words in the same
                      place is the exact "two opposite audiences, one page"
                      confusion `Reflections.jsx`'s header warns against. */}
                  <TabsTrigger value="match-reflections" data-testid="cp-tab-match-reflections">
                    <ClipboardList size={14} className="mr-1.5" />Match Reflections ({matchReflections.length})
                  </TabsTrigger>
                  {/* Issue #205. Rendered for admin too — with an
                      explanation instead of a list — rather than hidden:
                      admin being unable to read reflections is a decision
                      worth stating in the product, not a surface that
                      silently isn't there. */}
                  <TabsTrigger value="reflections" data-testid="cp-tab-reflections">
                    <EyeOff size={14} className="mr-1.5" />Reflections{isCoach ? ` (${reflections.length})` : ""}
                  </TabsTrigger>
                </TabsList>

                {/* Issue #292 — the player's own words, read-only, and only
                    the sections they chose to share. The panel renders no
                    trace of a section that is absent: the API cannot tell a
                    coach whether an omitted section was withheld or never
                    written, and a placeholder here would hand back exactly
                    that distinction. */}
                <TabsContent value="identity">
                  <PlayerIdentityPanel
                    playerId={selected.player_id}
                    playerName={selected.full_name}
                    viewerRole={user?.role}
                  />
                </TabsContent>

                {/* Goals tab (with editable progress) */}
                <TabsContent value="goals">
                  <CoachGoalsPanel
                    goals={goals}
                    sort={goalSort}
                    onSortChange={setGoalSort}
                    editingGoal={editingGoal}
                    onBeginEdit={beginEditGoal}
                    onEditingGoalChange={setEditingGoal}
                    onSaveProgress={saveGoalProgress}
                    onCancelEdit={() => setEditingGoal(null)}
                    onDelete={deleteGoal}
                    onNewGoal={openGoalDialog}
                  />
                </TabsContent>

                {/* Notes tab */}
                <TabsContent value="feedback">
                  <CoachFeedbackPanel
                    notes={notes}
                    sort={noteSort}
                    onSortChange={setNoteSort}
                    filterCat={filterCat}
                    onFilterChange={setFilterCat}
                    categories={CATEGORIES}
                    canMutateNote={canMutateNote}
                    onTogglePin={togglePin}
                    onDelete={deleteNote}
                    feedback={feedback}
                    onFeedbackChange={setFeedback}
                    category={category}
                    onCategoryChange={setCategory}
                    onAdd={addNote}
                    focusNonce={feedbackFocus}
                    onFocusHandled={() => setFeedbackFocus(0)}
                  />
                </TabsContent>

                {/* Issue #382 — the assign surface.

                    The picker offers `guideCatalogue`, which is the PUBLISHED
                    list and nothing else. That is not what stops an
                    unpublished guide being assigned — the API is, and it
                    refuses regardless of what this form posts. See
                    `api/src/routes/learnGuides.ts`. */}
                <TabsContent value="guides">
                  {/* Issue #526 — this tab STACKS three distinct sections
                      (assign picker, already-assigned, the drill block), so
                      each folds individually and the tab reads as a menu —
                      the #378 pattern where sections stack, deliberately NOT
                      applied inside the single-section tabs above (their tab
                      labels already carry counts). Every fold's state lives
                      inside its SectionCard, and each card is keyed by the
                      selected player, so a roster click arrives folded and
                      one player's open sections never leak onto the next. */}
                  <div className="space-y-4">
                  <SectionCard
                    key={`guide-assign-${selected.player_id}`}
                    icon={BookOpen}
                    title="Assign a guide"
                    desc={`${selected.full_name} sees it on Training & Guides, with your name on it.`}
                    collapsible
                    defaultOpen={false}
                    summary={catalogueStatus === "loaded" ? `${guideCatalogue.length} published` : "—"}
                    toggleTestid="cp-guide-assign-toggle"
                    testid="cp-guide-assign-card"
                  >
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Select value={guideToAssign} onValueChange={setGuideToAssign}>
                          <SelectTrigger className={`flex-1 ${TOUCH}`} data-testid="cp-guide-select">
                            <SelectValue placeholder="Choose a published guide" />
                          </SelectTrigger>
                          {/* #511: `position="item-aligned"`, not the shadcn
                              default popper. The per-player panels above
                              (health, career snapshot) load late and reflow
                              the page; the popper follows its anchor (Radix
                              autoUpdate), so a menu opened mid-reflow got
                              dragged below the viewport with page scroll
                              locked — a coach could open a picker they
                              cannot see (journey 21's 60s timeout, and real
                              phone UX). Item-aligned is Radix's own
                              in-viewport mode: it positions ONCE at open,
                              clamped inside the viewport (CONTENT_MARGIN in
                              @radix-ui/react-select), and never follows a
                              later reflow — a menu opened over a visible
                              trigger stays where the coach saw it. */}
                          <SelectContent position="item-aligned">
                            {/* #503: labelled category groups, not one flat
                                run — at 91 published guides a flat list put
                                nutrition unlabelled at the very bottom
                                (Martin's report). The catalogue arrives
                                sorted category-then-order (learnGuides.ts),
                                so grouping is one in-order pass. Each item
                                keeps its `category · title` text because the
                                collapsed SelectValue shows only that text,
                                and two title pairs (Leg Spin, Off Spin)
                                exist in both bowling and facing-bowling —
                                title alone would be ambiguous once the group
                                label is out of view. The `category/slug`
                                VALUE is unchanged: assignment parses it. */}
                            {[...new Set(guideCatalogue.map((g) => g.category))].map((category) => (
                              <SelectGroup key={category} data-testid={`cp-guide-group-${category}`}>
                                <SelectLabel className="capitalize">{category.replace(/-/g, " ")}</SelectLabel>
                                {guideCatalogue.filter((g) => g.category === category).map((g) => (
                                  <SelectItem key={`${g.category}/${g.slug}`} value={`${g.category}/${g.slug}`}>
                                    {g.category.replace(/-/g, " ")} · {g.title}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={assignSelectedGuide}
                          disabled={!guideToAssign || assigningGuide}
                          className={TOUCH}
                          style={{ background: "#0B2E59" }}
                          data-testid="cp-assign-guide-btn"
                        >
                          <Send size={14} className="mr-1.5" /> {assigningGuide ? "Assigning…" : "Assign"}
                        </Button>
                      </div>

                      {/* #511: a failed catalogue fetch is stated as a
                          failure with a retry — never rendered as the empty
                          claim below, which would be a statement about a
                          list this page failed to fetch. */}
                      {catalogueStatus === "failed" && (
                        <LoadFailureNote
                          what="the guide catalogue"
                          testId="cp-guide-catalogue-error"
                          onRetry={() => setCatalogueRetryNonce((n) => n + 1)}
                        />
                      )}

                      {/* Stated, not implied: a coach who cannot find a guide
                          in the picker should know why it is missing rather
                          than conclude the feature is broken. Gated on
                          "loaded" (#511): this line is a claim about the
                          catalogue, so it may only render once the catalogue
                          has actually come back. */}
                      {catalogueStatus === "loaded" && guideCatalogue.length === 0 && (
                        <p className="text-xs text-slate-500" data-testid="cp-no-published-guides">
                          No guides are published yet. A guide can only be assigned once it has been reviewed.
                        </p>
                      )}
                  </SectionCard>

                  <SectionCard
                    key={`guide-assigned-${selected.player_id}`}
                    icon={BookOpen}
                    title="Already assigned"
                    desc={`The guides ${selected.full_name.split(" ")[0]} already has, and who assigned each.`}
                    collapsible
                    defaultOpen={false}
                    count={assignedGuides.length}
                    toggleTestid="cp-assigned-toggle"
                    testid="cp-assigned-guides-card"
                  >
                      <div className="space-y-2" data-testid="cp-assigned-guides">
                        {assignedGuides.length === 0 && (
                          <p className="text-sm text-slate-500">Nothing assigned yet.</p>
                        )}
                        {assignedGuides.map((a) => (
                          <div
                            key={a.assignment_id}
                            className="rounded-xl border border-slate-200 p-3 flex items-center gap-3"
                            data-testid={`cp-assigned-${a.category}-${a.slug}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm text-slate-900">{a.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                Assigned by {a.assigned_by_coach_name}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeAssignedGuide(a)}
                              aria-label={`Remove ${a.title}`}
                              data-testid={`cp-unassign-${a.category}-${a.slug}`}
                            >
                              <Trash2 size={14} className="text-slate-400" />
                            </Button>
                          </div>
                        ))}
                      </div>
                  </SectionCard>

                  {/* Issue #461 — the coach's drills for THIS player,
                      "alongside the existing Quick Reference guide" (the
                      issue's words — hence this tab, under the assign card,
                      not a new trigger: the phone-390 tab strip is already
                      at its measured limit, see the Reflections trigger
                      note above). #509: ASSIGNMENT ONLY since the author
                      form moved to the drill bank on the Training surface
                      (CoachDrillBankPanel on /learn?tab=training).
                      Self-contained: the panel owns its fetches and
                      writes, this page only says WHO — its #526 fold, and
                      the count on it, live inside the panel with the state
                      they report. */}
                  <CoachDrillsPanel playerId={selected.player_id} playerName={selected.full_name} />
                  </div>
                </TabsContent>

                {/* Issue #376(b) — read-only: the player's own writing
                    about their own innings. See the panel's own header. */}
                <TabsContent value="match-reflections">
                  <CoachMatchReflectionsPanel
                    reflections={matchReflections}
                    sort={matchReflectionSort}
                    onSortChange={setMatchReflectionSort}
                    firstName={selected.full_name.split(" ")[0]}
                  />
                </TabsContent>

                {/* Issue #205 — the coach's own observations, on a
                    separate surface from Feedback. See the panel's own
                    header for why the two never merge. */}
                <TabsContent value="reflections">
                  <CoachReflectionsPanel
                    reflections={reflections}
                    sort={reflectionSort}
                    onSortChange={setReflectionSort}
                    isCoach={isCoach}
                    firstName={selected.full_name.split(" ")[0]}
                    editing={editingReflection}
                    onEditingChange={setEditingReflection}
                    onBeginEdit={(r) => setEditingReflection({ reflection_id: r.reflection_id, body: r.body })}
                    onSaveEdit={saveReflectionEdit}
                    onDelete={deleteReflection}
                    body={reflectionBody}
                    onBodyChange={setReflectionBody}
                    onAdd={addReflection}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}

          {/* Issue #284. Before this, a coach nobody has linked to yet got a
              headed page and an empty column: `{selected && …}` above renders
              nothing without a roster, and the dashboard's primary button
              sends a brand-new coach straight here. The copy is Martin's,
              locked on the issue, and is deliberately mechanism-first — the
              player initiates from their Settings (`sendInvite()`,
              Settings.jsx) and the coach accepts on this page (the invites
              card above, `cp-invites-card`), which is the coach's ONLY
              discovery route for a pending share while #71 (transactional
              email) does not exist. Nothing here promises anything unshipped.

              Coach-only: admin reaches this route too, and "a player shares
              their record with you" is not true of admin, whose roster is
              every player rather than a set of links. Admin with an empty
              roster keeps today's behaviour.

              The invites card is unaffected and still renders above this —
              a pending invite is shown alongside the explanation of what to
              do with it, not instead of it.

              Issue #756: behind `newUi("coach-portal-empty")`, this branch
              renders `CoachPortalEmptyState` instead — the redrawn screen,
              which keeps this exact copy and the `cp-empty-roster`/
              `cp-empty-add-player-link` testids (see that file's header for
              what actually changes: the link/gold-action destination, and
              the invites section's always-rendered labelled-empty state).
              With the flag off, this shipped branch is byte-for-byte
              unchanged. */}
          {isCoach && rosterLoaded && players.length === 0 && (
            showEmptyPortalRedraw ? (
              <CoachPortalEmptyState
                invites={invites}
                inviteBusyId={inviteBusyId}
                onAcceptInvite={acceptInvite}
                onDeclineInvite={declineInvite}
              />
            ) : (
              <Card className="border-slate-200" data-testid="cp-empty-roster">
                <CardContent className="p-8 sm:p-12 text-center">
                  <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "#0B2E5920" }}>
                    <Users size={22} color="#0B2E59" />
                  </div>
                  <p className="font-semibold text-slate-900 mb-1">No players yet.</p>
                  <p className="text-slate-500 max-w-md mx-auto">
                    A player shares their record with you from their Settings — you'll get an invite here to accept. Once
                    you're linked, their goals and feedback, plus your own reflections, all live on this page.
                  </p>
                  {/* Issue #530: the other direction now exists — a coach can
                      invite a player by email (#381 shape 1, API since #518,
                      entry point in Settings' AddPlayerCard). Martin's #284
                      copy above stays verbatim (it is still true, and locked
                      on that issue); this ADDS the new mechanism where a new
                      coach actually lands, as #530 asked to consider, rather
                      than embedding a second copy of the form here. */}
                  <p className="text-slate-500 max-w-md mx-auto mt-3">
                    You can also invite a player yourself:{" "}
                    <Link to="/settings" className="font-semibold text-[#0B2E59] underline" data-testid="cp-empty-add-player-link">
                      add them by email from your Settings
                    </Link>
                    .
                  </p>
                </CardContent>
              </Card>
            )
          )}
        </div>
      </div>

      {/* Assign goal dialog (#460: the markup moved out; this page still
          owns the state, the POST and when it opens). */}
      {/* #1430: beside the squad the player's page owns this dialog (it is handed `goalDialog`), so this one is not drawn. */}
      {!splitOn && (
        <AssignGoalDialog
          open={goalDialog}
          onOpenChange={setGoalDialog}
          playerName={selected?.full_name}
          goal={newGoal}
          onGoalChange={setNewGoal}
          onSave={saveGoal}
        />
      )}
    </div>
  );
}

// #1221 slice 1: the kit's frame (the surface #1236 used on the guide page).
const COACH_PLAYER_KIT_FRAME = {
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};

/**
 * Issue #973 (D29/D35/D186) — the coach's one-player page's three possible
 * outcomes for an explicit `?player=<id>` on the coach-player leg: still
 * loading the roster, the id is not on it (D186.2), or it is
 * (`CoachPlayerScreen`, handed every prop and handler `CoachPortal`'s own
 * render body already owns). A sibling function in THIS file, not a new
 * one — see the call site's own comment for why.
 */
function renderCoachPlayerRoute({
  wantedPlayerId, rosterLoaded, players, user,
  goals, goalsStatus, retryGoals, goalSort, setGoalSort, editingGoal, beginEditGoal, setEditingGoal, putGoalProgress, removeGoal, openGoalDialog,
  notes, notesStatus, retryNotes, noteSort, setNoteSort, filterCat, setFilterCat, canMutateNote, togglePin, removeNote,
  feedback, setFeedback, category, setCategory, postNote, feedbackFocus, setFeedbackFocus,
  guideCatalogue, catalogueStatus, setCatalogueRetryNonce, assignedGuides, guideToAssign, setGuideToAssign,
  assigningGuide, assignSelectedGuide, removeAssignedGuide,
  matchReflections, matchReflectionsStatus, retryMatchReflections, matchReflectionSort, setMatchReflectionSort,
  reflections, reflectionsStatus, retryReflections, reflectionSort, setReflectionSort, isCoach,
  editingReflection, setEditingReflection, patchReflection, removeReflection,
  reflectionBody, setReflectionBody, postReflection,
  tab, goToTab, goalDialog, setGoalDialog, newGoal, setNewGoal, createGoal, besideSquad,
}) {
  const backLink = besideSquad ? null : (
    <TextLink
      as={Link}
      to="/coach-portal"
      className="gap-1.5 text-sm font-semibold text-[#0B2E59]"
      data-testid="cp-player-back"
    >
      <ChevronLeft size={16} aria-hidden="true" />
      {coachPlayersName()}
    </TextLink>
  );

  // Held until the roster answers — the same reason `CoachPlayerRecord.jsx`
  // holds its own `rosterState === "loading"` (that file's header):
  // rendering the D186.2 not-found card before the roster has actually
  // come back would call an unlinked coach's own player "not found" before
  // the one read that could say otherwise has even gone out.
  if (!rosterLoaded) {
    return (
      <div className="space-y-6" data-testid="coach-portal-page">
        {backLink}
        {/* #1221 slice 1 (D254 Q15): the kit's frame, not a shadcn `Card`. */}
        <div className="p-6 text-sm text-slate-500" style={COACH_PLAYER_KIT_FRAME} data-testid="cp-player-loading">Loading…</div>
      </div>
    );
  }

  const onRoster = players.find((p) => p.player_id === wantedPlayerId) || null;

  // D186.2 (Andreas) — unlinked, revoked or a nonexistent id are ONE
  // outcome, identical in every case: no player data is read (this return
  // renders before any health/stats/matches request could ever be issued
  // for `wantedPlayerId`), and the same generic card the shipped
  // `/coach-portal/record` route already ships
  // (`CoachPlayerRecord.jsx:176-188`, `cpr-not-found`) — the same
  // anti-enumeration wording `api/src/routes/authz.ts` enforces on the
  // wire, said here for the same reason.
  if (!onRoster) {
    return (
      <div className="space-y-6" data-testid="coach-portal-page">
        {backLink}
        <div className="p-6 text-sm text-slate-600" style={COACH_PLAYER_KIT_FRAME} data-testid="cpr-not-found">
          There is no record here for you to read. A player's record opens once they have an active
          link with you — ask them to share, or pick someone from {coachPlayersName()}.
        </div>
      </div>
    );
  }

  return (
    <CoachPlayerScreen
      key={onRoster.player_id}
      player={onRoster}
      viewerRole={user?.role}
      besideSquad={besideSquad}
      goals={goals}
      goalsStatus={goalsStatus}
      onRetryGoals={retryGoals}
      goalSort={goalSort}
      onGoalSortChange={setGoalSort}
      editingGoal={editingGoal}
      onBeginEditGoal={beginEditGoal}
      onEditingGoalChange={setEditingGoal}
      onSaveGoalProgress={putGoalProgress}
      onCancelEditGoal={() => setEditingGoal(null)}
      onRemoveGoal={removeGoal}
      onOpenGoalDialog={openGoalDialog}
      notes={notes}
      notesStatus={notesStatus}
      onRetryNotes={retryNotes}
      noteSort={noteSort}
      onNoteSortChange={setNoteSort}
      filterCat={filterCat}
      onFilterCatChange={setFilterCat}
      categories={CATEGORIES}
      canMutateNote={canMutateNote}
      onTogglePinNote={togglePin}
      onDeleteNote={removeNote}
      feedback={feedback}
      onFeedbackChange={setFeedback}
      category={category}
      onCategoryChange={setCategory}
      onAddNote={postNote}
      feedbackFocus={feedbackFocus}
      onFeedbackFocusHandled={() => setFeedbackFocus(0)}
      guideCatalogue={guideCatalogue}
      catalogueStatus={catalogueStatus}
      onRetryCatalogue={() => setCatalogueRetryNonce((n) => n + 1)}
      assignedGuides={assignedGuides}
      guideToAssign={guideToAssign}
      onGuideToAssignChange={setGuideToAssign}
      assigningGuide={assigningGuide}
      onAssignGuide={assignSelectedGuide}
      onRemoveAssignedGuide={removeAssignedGuide}
      matchReflections={matchReflections}
      matchReflectionsStatus={matchReflectionsStatus}
      onRetryMatchReflections={retryMatchReflections}
      matchReflectionSort={matchReflectionSort}
      onMatchReflectionSortChange={setMatchReflectionSort}
      reflections={reflections}
      reflectionsStatus={reflectionsStatus}
      onRetryReflections={retryReflections}
      reflectionSort={reflectionSort}
      onReflectionSortChange={setReflectionSort}
      isCoach={isCoach}
      editingReflection={editingReflection}
      onEditingReflectionChange={setEditingReflection}
      onBeginEditReflection={(r) => setEditingReflection({ reflection_id: r.reflection_id, body: r.body })}
      onSaveReflectionEdit={patchReflection}
      onDeleteReflection={removeReflection}
      reflectionBody={reflectionBody}
      onReflectionBodyChange={setReflectionBody}
      onAddReflection={postReflection}
      tab={tab}
      onGoToTab={goToTab}
      goalDialog={goalDialog}
      onGoalDialogChange={setGoalDialog}
      newGoal={newGoal}
      onNewGoalChange={setNewGoal}
      onSaveGoal={createGoal}
    />
  );
}

function defaultGoal() {
  return {
    goal_title: "",
    target: "",
    description: "",
    category: "batting",
    horizon: "short",
    target_date: "",
    status: "not_started",
    progress_percentage: 0,
    milestones: [],
  };
}

